import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaMapMarkerAlt, FaUser, FaPhone, FaShoppingCart, FaArrowLeft, FaCheckCircle, FaChevronDown, FaTimes } from 'react-icons/fa';
import api from '../api';
import logoImage from '../../assets/logo_v3.png';
import './OrderPlacement.css';

export default function OrderPlacement() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedStore, userLocation } = location.state || {};
  
  const [formData, setFormData] = useState({
    name: [],
    address: '',
    user_phone: '',
    user_name: '',
    latitude: userLocation?.latitude || 0,
    longitude: userLocation?.longitude || 0
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const availableServices = [
    'Dry Cleaning',
    'Wash & Fold', 
    'Wash & Iron',
    'Shoe Cleaning',
    'Sofa Cleaning',
    'Steam Ironing',
    'Household Items',
    'Carpet Cleaning'
  ];

  useEffect(() => {
    if (!selectedStore) {
      navigate('/order/stores');
    }
  }, [selectedStore, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.multiselect-container')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      name: prev.name.includes(service) 
        ? prev.name.filter(s => s !== service)
        : [...prev.name, service]
    }));
  };

  const removeService = (service) => {
    setFormData(prev => ({
      ...prev,
      name: prev.name.filter(s => s !== service)
    }));
  };

  const clearAllServices = () => {
    setFormData(prev => ({
      ...prev,
      name: []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.length || !formData.address || !formData.user_phone || !formData.user_name) {
      alert('Please fill in all required fields and select at least one service');
      return;
    }

    setSubmitting(true);

    try {
      const orderPayload = {
        ...formData,
        name: formData.name.join(', '), // Convert array to comma-separated string
        store_id: selectedStore.id,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      };

      const response = await api.post('/createOrder', orderPayload);

      if (response.data?.status?.statusCode === 'code-200') {
        setOrderData(response.data.data);
        setOrderPlaced(true);
      } else {
        alert('Failed to place order. Please try again.');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToStores = () => {
    navigate('/order/stores');
  };

  const handleNewOrder = () => {
    setOrderPlaced(false);
    setOrderData(null);
    setFormData({
      name: [],
      address: '',
      user_phone: '',
      user_name: '',
      latitude: userLocation?.latitude || 0,
      longitude: userLocation?.longitude || 0
    });
  };

  if (!selectedStore) {
    return null;
  }

  if (orderPlaced) {
    return (
      <div className="order-success-page">
        <div className="success-container">
          <div className="success-icon">
            <FaCheckCircle />
          </div>
          <h1>Order Placed Successfully!</h1>
          <p>Your order has been confirmed and sent to {selectedStore.store_name}</p>
          
          <div className="order-details">
            <h3>Order Details</h3>
            <div className="detail-item">
              <strong>Order ID:</strong> #{orderData?.id}
            </div>
            <div className="detail-item">
              <strong>Store:</strong> {selectedStore.store_name}
            </div>
            <div className="detail-item">
              <strong>Address:</strong> {selectedStore.address}
            </div>
            <div className="detail-item">
              <strong>Distance:</strong> {selectedStore.distance_km} km
            </div>
            <div className="detail-item">
              <strong>Status:</strong> Pending
            </div>
          </div>

          <div className="success-actions">
            <button onClick={handleNewOrder} className="new-order-btn">
              Place Another Order
            </button>
            <button onClick={() => navigate('/')} className="home-btn">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-placement-page">
      <div className="order-container">
        {/* Header */}
        <div className="page-header">
          <button 
            className="back-btn"
            onClick={handleBackToStores}
            aria-label="Go back to stores"
          >
            <FaArrowLeft />
          </button>
          <h1>Place Your Order</h1>
          <p>Fill in your details to place an order with {selectedStore.store_name}</p>
        </div>

        {/* Simple Branding Section */}
        <div className="simple-branding">
          <div className="branding-content">
            <div className="brand-text">
              <h3>NeatnClean</h3>
              <p>Professional Cleaning Services</p>
            </div>
          </div>
        </div>

        {/* Selected Store Info */}
        <div className="selected-store-card">
          <div className="store-header">
            <div className="store-icon">
              <FaMapMarkerAlt />
            </div>
            <div className="store-info">
              <h3>{selectedStore.store_name}</h3>
              <p>{selectedStore.address}</p>
              <span className="distance">{selectedStore.distance_km} km away</span>
            </div>
          </div>
        </div>

        {/* Order Form */}
        <form className="order-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Your Details</h3>
            
            <div className="form-group">
              <label htmlFor="user_name">
                <FaUser />
                Full Name *
              </label>
              <input
                type="text"
                id="user_name"
                name="user_name"
                value={formData.user_name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="user_phone">
                <FaPhone />
                Phone Number *
              </label>
              <input
                type="tel"
                id="user_phone"
                name="user_phone"
                value={formData.user_phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Service Details</h3>
            
            <div className="form-group">
              <label htmlFor="services">
                <FaShoppingCart />
                Select Services * (Choose one or more)
              </label>
              <div className="multiselect-container">
                <div 
                  className={`multiselect-dropdown ${dropdownOpen ? 'open' : ''}`}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <div className="multiselect-selected">
                    {formData.name.length === 0 ? (
                      <span className="placeholder-text">Select services...</span>
                    ) : (
                      <div className="selected-services">
                        {formData.name.length <= 2 ? (
                          formData.name.map((service, index) => (
                            <span key={service} className="service-tag">
                              {service}
                              <button 
                                type="button"
                                className="remove-service"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeService(service);
                                }}
                              >
                                <FaTimes />
                              </button>
                            </span>
                          ))
                        ) : (
                          <>
                            <span className="service-tag">
                              {formData.name[0]}
                              <button 
                                type="button"
                                className="remove-service"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeService(formData.name[0]);
                                }}
                              >
                                <FaTimes />
                              </button>
                            </span>
                            <span className="more-count">+{formData.name.length - 1} more</span>
                          </>
                        )}
                        {formData.name.length > 0 && (
                          <button 
                            type="button"
                            className="clear-all"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearAllServices();
                            }}
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    )}
                    <FaChevronDown className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
                  </div>
                  <div className={`multiselect-options ${dropdownOpen ? 'open' : ''}`}>
                    {availableServices.map((service) => (
                      <label 
                        key={service} 
                        className={`multiselect-option ${formData.name.includes(service) ? 'selected' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={formData.name.includes(service)}
                          onChange={() => handleServiceToggle(service)}
                        />
                        <span className="service-name">{service}</span>
                        {formData.name.includes(service) && <FaCheckCircle className="check-icon" />}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">
                <FaMapMarkerAlt />
                Pickup Address *
              </label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter your complete address for pickup"
                rows={3}
                required
              />
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="place-order-btn"
              disabled={submitting}
            >
              {submitting ? 'Placing Order...' : 'Place Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
